import styled from "styled-components/macro";
import { TextField } from "../shared/TextField/TextField";
import { CheckMark } from "../../assets/iconComponents/CheckMark";
import { Password } from "../shared/TextField/Password";
import { DeviceType } from "../../utils/hooks/useDeviceHook";

const formFieldWidth = (deviceType: string) => {
  if (deviceType === "desktop") return "447px";
  if (deviceType === "tablet") return "421px";
  return "327px";
};

export const CreateAccountLoginMessageContainer = styled.div`
  display: inline-grid;
  grid-template-columns: auto auto;
  grid-gap: 10px;
  padding-top: 5px;
  padding-bottom: 10px;
`;

export const FormFieldInput = styled.div<{
  deviceType: DeviceType;
}>`
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
`;

export const CreateAccountEmailFormatContainer = styled.div``;

export const CreateAccountDesktopContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: ${({ theme }) => theme.white};
`;

export const CreateAccountDesktopFormCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
`;

export const CreateAccountDesktopColorBackground = styled.div`
  position: relative;
  min-height: 100vh;
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.loginBrandPanel};
`;

export const CreateAccountDesktopGradientLayer = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.gradient};
  transform: rotate(180deg) scaleX(-1);
  transform-origin: center center;
`;

export const CreateAccountDesktopBrandPanel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 1;
  padding: 0 40px 0 clamp(24px, 4vw, 55px);
  box-sizing: border-box;
  pointer-events: none;

  & img {
    pointer-events: auto;
  }
`;

export const CreateAccountDesktopBrandRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
`;

export const CreateAccountDesktopBrandTitleAside = styled.div`
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  min-width: 0;
  padding-left: 10px;
`;

export const CreateAccountDesktopBrandLockup = styled.img`
  display: block;
  height: 93px;
  width: auto;
  max-width: min(228px, 42vw);
  flex-shrink: 0;
`;

export const CreateAccountDesktopBrandTitleImg = styled.img`
  display: inline-block;
  vertical-align: baseline;
  height: 12px;
  width: auto;
  max-width: min(167px, 36vw);
`;

export const CreateAccountNonDesktopShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background-color: ${({ theme }) => theme.white};
`;

export const CreateAccountHeroBand = styled.div<{ $deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  gap: ${({ $deviceType }) => ($deviceType === "mobile" ? "12px" : "24px")};
  padding-left: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "16px" : "24px"};
  padding-right: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "16px" : "24px"};
  padding-bottom: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "12px" : "24px"};
  padding-top: ${({ $deviceType }) =>
    $deviceType === "mobile"
      ? "calc(32px + env(safe-area-inset-top, 0px))"
      : "calc(40px + env(safe-area-inset-top, 0px))"};
  background: ${({ theme, $deviceType }) =>
    $deviceType === "mobile"
      ? theme.loginHeroGradientMobile
      : theme.loginHeroGradient};
`;

export const CreateAccountHeroLockupImg = styled.img<{
  $deviceType: DeviceType;
}>`
  display: block;
  height: ${({ $deviceType }) => ($deviceType === "mobile" ? "28px" : "40px")};
  width: auto;
  max-width: 100%;
`;

export const CreateAccountHeroTitleImg = styled.img<{
  $deviceType: DeviceType;
}>`
  display: block;
  height: ${({ $deviceType }) => ($deviceType === "mobile" ? "11px" : "13px")};
  width: auto;
  max-width: min(200px, 72vw);
`;

export const CreateAccountNonDesktopContents = styled.div<{
  $deviceType: DeviceType;
}>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding-top: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "32px" : "80px"};
  padding-left: ${({ $deviceType }) =>
    $deviceType === "tablet" ? "clamp(24px, 12vw, 96px)" : "16px"};
  padding-right: ${({ $deviceType }) =>
    $deviceType === "tablet" ? "clamp(24px, 12vw, 96px)" : "16px"};
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
`;

export const CreateAccountPasswordRequirmentsTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  text-align: left;
  grid-gap: 7px;
  padding: 0 1em 1em 1em;
`;

export const CreateAccountPasswordRequirmentsContainer = styled.div`
  display: inline-grid;
  grid-template-columns: min-content auto;
  place-items: start;
  grid-gap: 7px;
`;

export const CreateAccountStyledCheckMark = styled(CheckMark)<{
  $valid: boolean;
}>`
  margin-top: 4px;
  path {
    fill: ${({ $valid, theme }) =>
      $valid ? theme.greenDark : theme.greyLight} !important;
  }
`;

export const CreateAccountButtonContainer = styled.div`
  display: inline-grid;
  grid-template-columns: 1fr;
  place-items: center;
`;

export const CreateAccountCenterContainer = styled.div<{
  deviceType: string;
}>`
  position: relative;
  display: flex;
  background-color: white;
  height: 100%;
  width: 100%;
`;

export const CreateAccountCenterFormContainer = styled.div`
  display: grid;
  margin: auto;
  grid-gap: 5px;
  padding: 10px;
  place-items: center;
  grid-template-rows: auto auto auto auto auto 1fr;
`;

export const CreateAccountCenterContainerPaddedTop = styled.div<{
  isDesktop: boolean;
}>`
  margin: auto;
  max-width: 480px;
  width: 100%;
  display: grid;
  grid-gap: 5px;
  padding: 10px;
  box-sizing: border-box;
  place-items: center;
  align-content: center;
  text-align: center;
`;

export const CreateAccountGeneralContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${({ theme }) => theme.boxShadow};
  max-width: 802px;
`;

export const CreateAccountDatePickerContainer = styled.div`
  width: 100%;
  height: 57px;
`;

export const CreateAccountErrorText = styled.div<{
  deviceType: DeviceType;
}>`
  color: ${({ theme }) => theme.redDark};
  font-size: 12px;
  margin-top: ${({ deviceType }) =>
    deviceType === "mobile" ? "-11px" : "-20px"};
`;

export const CreateAccountErrorTextDob = styled.div<{
  deviceType: DeviceType;
}>`
  color: ${({ theme }) => theme.redDark};
  font-size: 12px;
  margin-top: ${({ deviceType }) =>
    deviceType === "mobile" ? "-11px" : "-20px"};
  margin-left: 8px;
`;

export const CreateAccountErrorMessage = styled.div`
  margin-top: -42px;
`;

export const VerifyEmailSendAgainInput = styled(TextField)`
  width: 100%;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.greyLight};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.greenMedium};
  padding: 14px;
  padding-top: 17px;
  border-radius: 5px;
  margin-bottom: 40px;
`;

export const Spinner = styled.div`
  text-align: center;
  margin-top: 40%;
`;

export const CreateAccountFormStyled = styled.div<{
  deviceType: DeviceType;
}>`
  display: grid;
  grid-template-rows: 40px;
  grid-row-gap: ${({ deviceType }) =>
    deviceType === "mobile" ? "18px" : "31px"};
  padding-top: 12px;
  padding-bottom: 40px;
  place-items: center;
`;

export const CreateAccountInput = styled(TextField)`
  width: 100%;
`;

export const CreateAccountPassword = styled(Password)`
  width: 100%;
`;

export const CreateAccountPasswordInfoContainer = styled.div`
  width: 100%;
  margin-top: 10px;
`;

export const CreateAccountFormErrorContainer = styled.div`
  width: 100%;
`;

export const CreateAccountFormStyledContainer = styled.form<{
  deviceType: DeviceType;
}>`
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
`;

export const ButtonContainer = styled.div`
  margin-top: 10px;
`;

export const CreateAccountPrivacyPolicy = styled.div`
  margin-top: 5px;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.greyLight};

  a {
    color: ${({ theme }) => theme.greyLight};
  }
`;
